import React, { Fragment, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, CheckCircle, ChevronRight, Lock, Send } from 'lucide-react'
import { Progress } from '../ui/ProgressBar'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/Dialog'
import { Textarea } from '../ui/Textarea'
import { Label } from '../ui/Label'
import { Button } from '../ui/Button'
import type { LucideIcon } from 'lucide-react'

export type HubColor = 'green' | 'blue' | 'purple' | 'orange' | 'cyan'

const colorClasses: Record<HubColor, {
  icon: string
  badge: string
  progress: string
  submitBtn: string
}> = {
  green: {
    icon: 'text-green-600 dark:text-green-400',
    badge: 'text-green-600 dark:text-green-400',
    progress: '[&>div]:!bg-gradient-to-r [&>div]:!from-green-600 [&>div]:!via-green-500 [&>div]:!to-green-600 [&>div]:!shadow-[0_0_10px_rgba(34,197,94,0.6)]',
    submitBtn: 'bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white',
  },
  blue: {
    icon: 'text-blue-600 dark:text-blue-400',
    badge: 'text-blue-600 dark:text-blue-400',
    progress: '[&>div]:!bg-gradient-to-r [&>div]:!from-blue-600 [&>div]:!via-blue-500 [&>div]:!to-blue-600 [&>div]:!shadow-[0_0_10px_rgba(59,130,246,0.6)]',
    submitBtn: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white',
  },
  purple: {
    icon: 'text-purple-600 dark:text-purple-400',
    badge: 'text-purple-600 dark:text-purple-400',
    progress: '[&>div]:!bg-gradient-to-r [&>div]:!from-purple-600 [&>div]:!via-purple-500 [&>div]:!to-purple-600 [&>div]:!shadow-[0_0_10px_rgba(168,85,247,0.6)]',
    submitBtn: 'bg-purple-600 hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700 text-white',
  },
  orange: {
    icon: 'text-orange-600 dark:text-orange-400',
    badge: 'text-orange-600 dark:text-orange-400',
    progress: '[&>div]:!bg-gradient-to-r [&>div]:!from-orange-600 [&>div]:!via-orange-500 [&>div]:!to-orange-600 [&>div]:!shadow-[0_0_10px_rgba(249,115,22,0.6)]',
    submitBtn: 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-600 dark:hover:bg-orange-700 text-white',
  },
  cyan: {
    icon: 'text-cyan-600 dark:text-cyan-400',
    badge: 'text-cyan-600 dark:text-cyan-400',
    progress: '[&>div]:!bg-gradient-to-r [&>div]:!from-cyan-600 [&>div]:!via-cyan-500 [&>div]:!to-cyan-600 [&>div]:!shadow-[0_0_10px_rgba(6,182,212,0.6)]',
    submitBtn: 'bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-600 dark:hover:bg-cyan-700 text-white',
  },
}

export function getHubColorClasses(color: HubColor) {
  return colorClasses[color]
}

interface HubShellHeaderProps {
  icon: React.ComponentType<{ className?: string }>
  title: string
  subtitle: string
  completionPercent: number
  isSubmitted: boolean
  color: HubColor
  onClose: () => void
}

export const HubShellHeader: React.FC<HubShellHeaderProps> = ({
  icon: Icon,
  title,
  subtitle,
  completionPercent,
  isSubmitted,
  color,
  onClose,
}) => {
  const colors = colorClasses[color]
  return (
    <div className="border-b border-slate-200 dark:border-slate-700 px-4 md:px-6 py-3 md:py-4 bg-white dark:bg-slate-800">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-3 min-w-0">
          <Icon className={cn('h-5 w-5 md:h-6 md:w-6 flex-shrink-0', colors.icon)} />
          <div className="min-w-0">
            <h2 className="text-base md:text-xl font-semibold text-slate-900 dark:text-slate-100 truncate">{title}</h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{subtitle}</p>
            {isSubmitted && (
              <div className="flex items-center space-x-2 mt-1">
                <CheckCircle className={cn('h-4 w-4', colors.icon)} />
                <span className={cn('text-xs font-medium', colors.badge)}>Completed & Read-Only</span>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-secondary-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        >
          <X className="h-5 w-5 text-secondary-600 dark:text-slate-400" />
        </button>
      </div>
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-600 dark:text-slate-400">Completion</span>
          <span className={cn('text-xs font-semibold', colors.badge)}>{completionPercent}%</span>
        </div>
        <Progress value={completionPercent} className={cn('h-2', colors.progress)} />
      </div>
    </div>
  )
}

interface HubShellFooterProps {
  isSubmitted: boolean
  canSubmit: boolean
  completedMessage: string
  nextHubMessage?: string
  onSubmit: () => void
  onClose: () => void
  color: HubColor
  submitLabel?: string
  submitIcon?: LucideIcon
  submitDisabledReason?: string
  lastSavedAt?: string | null
  showSaveIndicator?: boolean
  children?: React.ReactNode
}

export const HubShellFooter: React.FC<HubShellFooterProps> = ({
  isSubmitted,
  canSubmit,
  completedMessage,
  nextHubMessage,
  onSubmit,
  onClose,
  color,
  submitLabel = 'Submit Hub',
  submitIcon: SubmitIcon = Send,
  submitDisabledReason,
  lastSavedAt,
  showSaveIndicator = true,
  children,
}) => {
  const colors = colorClasses[color]
  return (
    <div className="border-t border-slate-200 dark:border-slate-700 px-4 md:px-6 py-3 md:py-4 bg-white dark:bg-slate-800">
      <div className="flex items-center justify-between gap-2">
        {isSubmitted ? (
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            <CheckCircle className={cn('h-5 w-5 flex-shrink-0', colors.icon)} />
            <div className="min-w-0">
              <p className={cn('text-sm font-semibold truncate', colors.badge)}>{completedMessage}</p>
              {nextHubMessage && (
                <p className={cn('text-xs hidden md:block', colors.badge)}>{nextHubMessage}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1" />
        )}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isSubmitted && showSaveIndicator && (
            <div className="flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-1 text-xs text-slate-600 dark:text-slate-300">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span>
                {lastSavedAt
                  ? `Auto-saved ${new Date(lastSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                  : 'Auto-save on'}
              </span>
            </div>
          )}
          {children}
          <button
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-medium px-4 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              colors.submitBtn
            )}
            onClick={onSubmit}
            disabled={isSubmitted || !canSubmit}
            title={
              !canSubmit && !isSubmitted
                ? (submitDisabledReason || 'Please complete all steps before submitting')
                : isSubmitted
                ? 'Submitted'
                : 'Submit'
            }
          >
            <SubmitIcon className="h-4 w-4" />
            {submitLabel}
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            title="Close"
          >
            <X className="h-4 w-4 mr-1" />
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export interface HubStep {
  key: string
  label: string
  icon: LucideIcon
  completed: boolean
  disabled?: boolean
  disabledReason?: string
}

export interface HubShellStep {
  id: string
  label: string
  icon: React.ReactNode
  complete?: boolean
}

interface StepTabsProps {
  steps: HubStep[]
  activeStep: string
  onStepChange: (key: string) => void
  sectionTitle: string
  color: HubColor
  isSubmitted?: boolean
}

export const StepTabs: React.FC<StepTabsProps> = ({
  steps,
  activeStep,
  onStepChange,
  sectionTitle,
  color,
  isSubmitted = false,
}) => {
  const colors = colorClasses[color]
  const activeIndex = steps.findIndex(s => s.key === activeStep)

  return (
    <div className="mb-3 border-b border-slate-200 dark:border-slate-700 pb-3 flex-shrink-0">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{sectionTitle}</h3>
        <span className="text-xs text-slate-500 dark:text-slate-500">
          Step {activeIndex + 1} of {steps.length}
        </span>
      </div>
      <div className="flex items-center space-x-1.5 overflow-x-auto pb-1">
        {steps.map((step, index) => {
          const Icon = step.icon
          const stepDisabled = !isSubmitted && (step.disabled ?? false)
          return (
            <Fragment key={step.key}>
              <button
                type="button"
                onClick={() => !stepDisabled && onStepChange(step.key)}
                disabled={stepDisabled}
                title={stepDisabled ? step.disabledReason : undefined}
                aria-disabled={stepDisabled}
                className={cn(
                  'flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap',
                  stepDisabled
                    ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                    : isSubmitted
                    ? activeStep === step.key
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                    : activeStep === step.key
                    ? cn(colors.submitBtn, 'shadow-sm')
                    : step.completed
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                )}
              >
                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="hidden sm:inline">{step.label}</span>
                {(step.completed || isSubmitted) && !stepDisabled && <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />}
              </button>
              {index < steps.length - 1 && (
                <ChevronRight className="h-3 w-3 text-slate-400 dark:text-slate-500 flex-shrink-0" />
              )}
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}

interface HubShellStepsProps {
  sectionLabel: string
  currentStep: number
  totalSteps: number
  steps: HubShellStep[]
  activeStepId: string
  onStepChange: (id: string) => void
  color: HubColor
}

export const HubShellSteps: React.FC<HubShellStepsProps> = ({
  sectionLabel,
  currentStep,
  totalSteps,
  steps,
  activeStepId,
  onStepChange,
  color,
}) => {
  const colors = colorClasses[color]
  return (
    <div className="mb-3 border-b border-slate-200 dark:border-slate-700 pb-3 flex-shrink-0">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{sectionLabel}</h3>
        <span className="text-xs text-slate-500 dark:text-slate-500">
          Step {currentStep} of {totalSteps}
        </span>
      </div>
      <div className="flex items-center space-x-1.5 overflow-x-auto pb-1">
        {steps.map((step, index) => (
          <Fragment key={step.id}>
            <button
              type="button"
              onClick={() => onStepChange(step.id)}
              className={cn(
                'flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap',
                activeStepId === step.id
                  ? cn(colors.submitBtn, 'shadow-sm')
                  : step.complete
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
              )}
            >
              <span className="h-3.5 w-3.5 flex-shrink-0">{step.icon}</span>
              <span className="hidden sm:inline">{step.label}</span>
              {step.complete && <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />}
            </button>
            {index < steps.length - 1 && (
              <ChevronRight className="h-3 w-3 text-slate-400 dark:text-slate-500 flex-shrink-0" />
            )}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

interface ConfirmationToggleProps {
  id: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  visible: boolean
  disabled?: boolean
}

export const ConfirmationToggle: React.FC<ConfirmationToggleProps> = ({
  id,
  label,
  checked,
  onChange,
  visible,
  disabled = false,
}) => {
  if (!visible) return null

  return (
    <div className="mt-4 mx-1">
      <div
        className={cn(
          'p-4 rounded-lg border-2 transition-all duration-300',
          disabled
            ? 'border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/30 opacity-60 cursor-not-allowed'
            : checked
            ? 'border-emerald-400 dark:border-emerald-600 bg-emerald-50/80 dark:bg-emerald-900/30'
            : 'border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-900/20'
        )}
      >
        <label
          htmlFor={id}
          className={cn(
            'flex items-center justify-between gap-4',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          <span
            className={cn(
              'text-sm font-medium',
              disabled
                ? 'text-slate-400 dark:text-slate-500'
                : 'text-emerald-900 dark:text-emerald-200'
            )}
          >
            {label}
          </span>
          <div className="relative flex-shrink-0">
            <input
              type="checkbox"
              id={id}
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="sr-only peer"
            />
            <div className="w-11 h-6 rounded-full bg-slate-300 dark:bg-slate-600 peer-checked:bg-emerald-600 transition-colors"></div>
            <div className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"></div>
          </div>
        </label>
      </div>
    </div>
  )
}

interface StepIntroNoteProps {
  icon?: LucideIcon
  title?: string
  body: React.ReactNode
  color: HubColor
}

const stepIntroNoteColorClasses: Record<HubColor, string> = {
  green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100',
  blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100',
  purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-900 dark:text-purple-100',
  orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-900 dark:text-orange-100',
  cyan: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800 text-cyan-900 dark:text-cyan-100',
}

const stepIntroNoteIconColorClasses: Record<HubColor, string> = {
  green: 'text-green-600 dark:text-green-400',
  blue: 'text-blue-600 dark:text-blue-400',
  purple: 'text-purple-600 dark:text-purple-400',
  orange: 'text-orange-600 dark:text-orange-400',
  cyan: 'text-cyan-600 dark:text-cyan-400',
}

export const StepIntroNote: React.FC<StepIntroNoteProps> = ({
  icon: Icon,
  title,
  body,
  color,
}) => {
  return (
    <div className={cn('rounded-lg border px-4 py-3 mb-4 flex items-start gap-3', stepIntroNoteColorClasses[color])}>
      {Icon && (
        <Icon className={cn('h-4 w-4 mt-0.5 flex-shrink-0', stepIntroNoteIconColorClasses[color])} />
      )}
      <div className="min-w-0">
        {title && (
          <p className="text-sm font-semibold mb-0.5">{title}</p>
        )}
        <p className="text-sm leading-relaxed">{body}</p>
      </div>
    </div>
  )
}

interface PrerequisiteBannerProps {
  icon: LucideIcon
  title: string
  message: string
  color: HubColor
  onClose: () => void
  prerequisiteHubName: string
}

const prerequisiteBannerColorClasses: Record<HubColor, {
  wrapper: string
  iconWrapper: string
  icon: string
  title: string
  body: string
  button: string
}> = {
  green: {
    wrapper: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    iconWrapper: 'bg-green-100 dark:bg-green-800/40',
    icon: 'text-green-600 dark:text-green-400',
    title: 'text-green-900 dark:text-green-100',
    body: 'text-green-800 dark:text-green-200',
    button: 'bg-green-600 hover:bg-green-700 text-white',
  },
  blue: {
    wrapper: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    iconWrapper: 'bg-blue-100 dark:bg-blue-800/40',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-900 dark:text-blue-100',
    body: 'text-blue-800 dark:text-blue-200',
    button: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  purple: {
    wrapper: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    iconWrapper: 'bg-purple-100 dark:bg-purple-800/40',
    icon: 'text-purple-600 dark:text-purple-400',
    title: 'text-purple-900 dark:text-purple-100',
    body: 'text-purple-800 dark:text-purple-200',
    button: 'bg-purple-600 hover:bg-purple-700 text-white',
  },
  orange: {
    wrapper: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    iconWrapper: 'bg-orange-100 dark:bg-orange-800/40',
    icon: 'text-orange-600 dark:text-orange-400',
    title: 'text-orange-900 dark:text-orange-100',
    body: 'text-orange-800 dark:text-orange-200',
    button: 'bg-orange-600 hover:bg-orange-700 text-white',
  },
  cyan: {
    wrapper: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800',
    iconWrapper: 'bg-cyan-100 dark:bg-cyan-800/40',
    icon: 'text-cyan-600 dark:text-cyan-400',
    title: 'text-cyan-900 dark:text-cyan-100',
    body: 'text-cyan-800 dark:text-cyan-200',
    button: 'bg-cyan-600 hover:bg-cyan-700 text-white',
  },
}

export const PrerequisiteBanner: React.FC<PrerequisiteBannerProps> = ({
  icon: Icon,
  title,
  message,
  color,
  onClose,
  prerequisiteHubName,
}) => {
  const classes = prerequisiteBannerColorClasses[color]
  return (
    <div
      className={cn('rounded-xl border-2 p-6 flex flex-col items-center text-center gap-4', classes.wrapper)}
      role="alert"
    >
      <div className={cn('p-4 rounded-full', classes.iconWrapper)}>
        <Icon className={cn('h-8 w-8', classes.icon)} aria-hidden="true" />
      </div>
      <div>
        <h3 className={cn('text-lg font-semibold mb-1', classes.title)}>{title}</h3>
        <p className={cn('text-sm leading-relaxed mb-1', classes.body)}>{message}</p>
        <p className={cn('text-xs font-medium', classes.body)}>
          Complete the <span className="font-semibold">{prerequisiteHubName}</span> first to unlock this section.
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className={cn(
          'inline-flex items-center justify-center rounded-md text-sm font-medium px-5 py-2 transition-colors',
          classes.button
        )}
      >
        Got it, take me back
      </button>
    </div>
  )
}

interface PrerequisiteDialogProps {
  open: boolean
  hubTitle: string
  message: string
  color: HubColor
  onClose: () => void
}

export const PrerequisiteDialog: React.FC<PrerequisiteDialogProps> = ({
  open,
  hubTitle,
  message,
  color,
  onClose,
}) => {
  const colors = colorClasses[color]
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) onClose() }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className={cn('p-2 rounded-lg', prerequisiteDialogIconColorClasses[color])}>
              <Lock className={cn('h-5 w-5', colors.icon)} aria-hidden="true" />
            </div>
            <DialogTitle className={colors.badge}>
              {hubTitle} locked
            </DialogTitle>
          </div>
          <DialogDescription>
            {message}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            onClick={onClose}
            className={colors.submitBtn}
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const prerequisiteDialogIconColorClasses: Record<HubColor, string> = {
  green: 'bg-green-100 dark:bg-green-900/30',
  blue: 'bg-blue-100 dark:bg-blue-900/30',
  purple: 'bg-purple-100 dark:bg-purple-900/30',
  orange: 'bg-orange-100 dark:bg-orange-900/30',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/30',
}

const requestEditModalIconColorClasses: Record<HubColor, string> = {
  green: 'bg-green-100 dark:bg-green-900/30',
  blue: 'bg-blue-100 dark:bg-blue-900/30',
  purple: 'bg-purple-100 dark:bg-purple-900/30',
  orange: 'bg-orange-100 dark:bg-orange-900/30',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/30',
}

const requestEditButtonColorClasses: Record<HubColor, string> = {
  green: 'border-green-300 dark:border-green-700',
  blue: 'border-blue-300 dark:border-blue-700',
  purple: 'border-purple-300 dark:border-purple-700',
  orange: 'border-orange-300 dark:border-orange-700',
  cyan: 'border-cyan-300 dark:border-cyan-700',
}

interface RequestEditModalProps {
  open: boolean
  color: HubColor
  onSubmit: (reason: string) => void
  onClose: () => void
}

export const RequestEditModal: React.FC<RequestEditModalProps> = ({
  open,
  color,
  onSubmit,
  onClose,
}) => {
  const [reason, setReason] = useState('')
  const colors = colorClasses[color]
  useEffect(() => {
    if (open) setReason('')
  }, [open])
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) onClose() }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className={cn('p-2 rounded-lg', requestEditModalIconColorClasses[color])}>
              <CheckCircle className={cn('h-5 w-5', colors.icon)} />
            </div>
            <div>
              <DialogTitle>Request Edit</DialogTitle>
              <DialogDescription>Submitted section</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="request-edit-reason">What changes do you need?</Label>
            <Textarea
              id="request-edit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please describe the edits or corrections needed..."
              rows={6}
              autoFocus
            />
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-400 dark:bg-slate-600 flex items-center justify-center">
                <ChevronRight className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
                  What happens next?
                </p>
                <p className="text-xs text-slate-700 dark:text-slate-300">
                  Our support team will review your request and contact you within 1-2 business days. You will receive a confirmation email with your ticket number.
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onSubmit(reason)}
            disabled={!reason.trim()}
            className={colors.submitBtn}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface RequestEditButtonProps {
  onRequest: (reason: string) => void
  color: HubColor
}

export const RequestEditButton: React.FC<RequestEditButtonProps> = ({
  onRequest,
  color,
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const colors = colorClasses[color]
  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={cn(
          'inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 border transition-colors',
          requestEditButtonColorClasses[color],
          colors.badge,
          'hover:bg-slate-50 dark:hover:bg-slate-700'
        )}
        aria-label="Request edit"
      >
        Request Edit
      </button>
      <RequestEditModal
        open={modalOpen}
        color={color}
        onSubmit={(reason) => {
          onRequest(reason)
          setModalOpen(false)
        }}
        onClose={() => setModalOpen(false)}
      />
    </>
  )
}

interface HubShellProps {
  icon: React.ComponentType<{ className?: string }>
  title: string
  subtitle: string
  completionPercent: number
  isSubmitted: boolean
  color: HubColor
  onClose: () => void
  canSubmit: boolean
  completedMessage: string
  nextHubMessage?: string
  onSubmit: () => void
  submitLabel?: string
  submitIcon?: LucideIcon
  submitDisabledReason?: string
  lastSavedAt?: string | null
  showSaveIndicator?: boolean
  footerExtra?: React.ReactNode
  children: React.ReactNode
}

export const HubShell: React.FC<HubShellProps> = ({
  children,
  footerExtra,
  ...props
}) => {
  return createPortal(
    <div className="modal-overlay">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <HubShellHeader
          icon={props.icon}
          title={props.title}
          subtitle={props.subtitle}
          completionPercent={props.completionPercent}
          isSubmitted={props.isSubmitted}
          color={props.color}
          onClose={props.onClose}
        />
        <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-slate-800 flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 md:p-6">
            {children}
          </div>
        </div>
        <HubShellFooter
          isSubmitted={props.isSubmitted}
          canSubmit={props.canSubmit}
          completedMessage={props.completedMessage}
          nextHubMessage={props.nextHubMessage}
          onSubmit={props.onSubmit}
          onClose={props.onClose}
          color={props.color}
          submitLabel={props.submitLabel}
          submitIcon={props.submitIcon}
          submitDisabledReason={props.submitDisabledReason}
          lastSavedAt={props.lastSavedAt}
          showSaveIndicator={props.showSaveIndicator}
        >
          {footerExtra}
        </HubShellFooter>
      </div>
    </div>,
    document.body
  )
}
